import { ChevronRightIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  ButtonProps,
  Center,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Image,
  Link,
  Select,
  SimpleGrid,
  Spacer,
  StackDivider,
  Text,
  useColorModeValue,
  useDisclosure,
  UseDisclosureReturn,
  VStack,
} from '@chakra-ui/react';
import { Prisma } from '@prisma/client';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import type { GetServerSideProps, NextPage } from 'next';
import NextHead from 'next/head';
import NextImage from 'next/image';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import * as yup from 'yup';
import getConfig, {
  getPublicRuntimeConfig
} from '../../common/config';
import { DEPARTMENT_ID_OTHER } from '../../common/constants';
import { IntegrationStatus, isAccountIntegrated } from '../../common/libs/accountIntegrationStatus';
import {
  AccountTypeString,
  string as accountTypeToString,
  isAntaaAccount,
  isSlideAccount,
  isSlideAndAntaaAccount,
} from '../../common/libs/accountType';
import { AntaaAccountSubType } from '../../common/libs/antaaAccountSubType';
import { AntaaAccountType } from '../../common/libs/antaaAccountType';
import {
  ArticleStatus,
  ArticleType,
  shouldNotShow,
} from '../../common/libs/article';
import { DiscloseScope } from '../../common/libs/discloseScope';
import { sendAnalyticsEvent } from '../../common/libs/googleAnalytics';
import { title } from '../../common/libs/metadata';
import {
  SORT_ORDER_LIKE_COUNT_DESC,
  SORT_ORDER_SESSION_COUNT_DESC,
  SORT_ORDER_UPDATE_DATE_DESC,
  SortOrder,
} from '../../common/libs/sortOrder';
import {
  createProfileUrl,
  createUserName,
  isCorporationAccount,
  isDoctorRecruiterAccount,
  isPharmaceuticalAccount,
  isPublisherAccount,
} from '../../common/libs/user';
import { hasDemoAuthority } from '../../common/libs/userRoleType';
import ArticlesGrid, { ArticleHeader } from '../../components/ArticlesGrid';
import type { Props as ArticleSummaryProps } from '../../components/ArticleSummary';
import BadgeModal from '../../components/BadgeModal';
import CommaFormattedNumber from '../../components/CommaFormattedNumber';
import ExternalLink from '../../components/ExternalLink';
import FullWidthBox from '../../components/FullWidthBox';
import { FaCrown, FaXTwitter, HiCheckBadge } from '../../components/Icons';
import { JsonLd } from '../../components/JsonLd';
import { useAppContext } from '../../contexts/app';
import useApiClient from '../../hooks/useApiClient';
import useIdApiClient from '../../hooks/useIdApiClient';
import * as date from '../../server/libs/date';
import { fetchAccountIntegrationProfiles } from '../../server/libs/id';
import prisma from '../../server/libs/prisma';
import withAuth from '../../server/libs/withAuth';

dayjs.extend(isBetween);

const SORT_TARGET_COLUMN_UPDATE_DATE = 'update_date';
const SORT_TARGET_COLUMN_SESSION_COUNT = 'session_count';
const SORT_TARGET_COLUMN_LIKE_COUNT = 'like_count';

type SortTargetColumn =
  | typeof SORT_TARGET_COLUMN_UPDATE_DATE
  | typeof SORT_TARGET_COLUMN_SESSION_COUNT
  | typeof SORT_TARGET_COLUMN_LIKE_COUNT;

type PageContext = {
  user: User;
  isMyProfile: boolean;
  departmentName: string | null;
  subDepartmentNames: string[];
  prefectureName: string | null;
  articles: ArticleHeader[];
  sortOrder: SortOrder;
  postedArticleCount: number;
  viewedArticleCount: number;
  likedArticleCount: number;
  awards: Award[];
  interviews: Interview[];
  isShowBadge: boolean;
  isDemoAccount: boolean;
  isCorporationAccount: boolean;
  isPharmaceuticalAccount: boolean;
  isPublisherAccount: boolean;
  isDoctorRecruiterAccount: boolean;
};

type User = {
  userId: number;
  discloseId: string | null;
  userName: string | null;
  lastName: string | null;
  firstName: string | null;
  hospital: string | null;
  prefectureId: number | null;
  speciality: string | null;
  subSpeciality: string | null;
  departmentId: number | null;
  twitterAccount: string | null;
  jobTitle: string | null;
  graduatedYear: string | null;
  hasProfileImage: boolean;
  idBaseProfileUrl: string | null;
  updateDateEpochMilliseconds: number;
  accountType: AccountTypeString | null;
  antaaAccountType?: string | null;
  antaaAccountSubType?: string | null;
  isOfficialAccount?: boolean | null;
  isPublisherAccount?: boolean | null;
  isDoctorRecruiterAccount?: boolean | null;
  accountIntegrationStatus: IntegrationStatus | null;
};

type Award = {
  id: number;
  name: string;
  imageFilename: string;
};

type Interview = {
  id: number;
  siteUrl: string;
  imageUrl: string;
};

function createProfileImageUrl(userId: number) {
  const { slideWebBaseUrl } = getPublicRuntimeConfig();
  const v = Math.floor(Date.now() / (10 * 60 * 1000)); // 10分ごとにURL切り替え
  return `${slideWebBaseUrl}/api/profile/${userId}/image?v=${v}`;
}

function createNameLabel({ user, fullName }: { user: User; fullName: string }) {
  let name = fullName;
  if (
    isSlideAccount(user.accountType ?? '') ||
    user.antaaAccountType !== AntaaAccountType.CORPORATION
  ) {
    const userAttribute = user.jobTitle || user.speciality;
    name += `（${user.hospital || ''}${
      userAttribute ? ` ${userAttribute}` : ''
    }）`;
  }
  return name;
}

function createDescription({
  user,
  departmentName,
  fullName,
}: {
  user: User;
  departmentName: string | null;
  fullName: string;
}) {
  const nameLabel = createNameLabel({ user, fullName });
  const departmentLabel = departmentName ? `専門は${departmentName}。` : '';
  return `${nameLabel}。${departmentLabel}プロフィールと投稿スライドを掲載。`;
}

function PageHead({
  user,
  departmentName,
  subDepartmentNames,
  fullName,
}: {
  user: User;
  departmentName: string | null;
  subDepartmentNames: string[];
  fullName: string;
}) {
  const profileUrl = createProfileUrl(
    user.discloseId ? undefined : user.userId,
    user.discloseId ?? undefined,
  );
  let profileImageUrl: string | undefined;
  if (user.hasProfileImage) {
    profileImageUrl = createProfileImageUrl(user.userId);
    if (user.idBaseProfileUrl) {
      profileImageUrl += `?idurl=${encodeURIComponent(user.idBaseProfileUrl)}`;
    }
  }
  const nameLabel = createNameLabel({ user, fullName });
  const description = createDescription({
    user,
    departmentName,
    fullName,
  });

  return (
    <>
      <NextHead>
        <title>
          {title(
            `${nameLabel}` + (departmentName ? ` - ${departmentName}` : ''),
          )}
        </title>
        <meta name="description" content={description} />
        <meta
          property="og:title"
          content={
            `${nameLabel}` + (departmentName ? ` - ${departmentName}` : '')
          }
          key="og:title"
        />
        <meta property="og:type" content="profile" key="og:type" />
        {!!profileImageUrl && (
          <meta property="og:image" content={profileImageUrl} key="og:image" />
        )}
        <meta property="og:url" content={profileUrl} key="og:url" />
        <meta
          property="og:description"
          content={description}
          key="og:description"
        />
        {!!profileImageUrl && (
          <meta name="twitter:image" content={profileImageUrl} />
        )}
        <link rel="canonical" href={profileUrl} />
      </NextHead>
      <JsonLdProfile
        user={user}
        departmentName={departmentName}
        subDepartmentNames={subDepartmentNames}
        fullName={fullName}
      />
    </>
  );
}

function JsonLdProfile({
  user,
  departmentName,
  subDepartmentNames,
  fullName,
}: {
  user: User;
  departmentName: string | null;
  subDepartmentNames: string[];
  fullName: string;
}) {
  const profileUrl = createProfileUrl(
    user.discloseId ? undefined : user.userId,
    user.discloseId ?? undefined,
  );
  let profileImageUrl: string | undefined;
  if (user.hasProfileImage) {
    profileImageUrl = createProfileImageUrl(user.userId);
    if (user.idBaseProfileUrl) {
      profileImageUrl += `?idurl=${encodeURIComponent(user.idBaseProfileUrl)}`;
    }
  }
  const nameLabel = createNameLabel({ user, fullName });
  const description = createDescription({
    user,
    departmentName,
    fullName,
  });

  const structuredData = {
    '@type': ['WebPage', 'ProfilePage'],
    url: profileUrl,
    name: title(
      `${nameLabel}` + (departmentName ? ` - ${departmentName}` : ''),
    ),
    description,
    mainEntity: [
      {
        '@type': 'Person',
        name: fullName,
        url: profileUrl,
        image: profileImageUrl,
        ...(user.jobTitle && { jobTitle: user.jobTitle }),
        worksFor: {
          '@type': 'MedicalOrganization',
          ...(user.hospital && { name: user.hospital }),
        },
        knowsAbout: [departmentName, ...subDepartmentNames].filter(Boolean), // departmentNameがnullの場合に `null` と表示されないようにフィルタリングする
        sameAs: [],
      },
    ],
  };
  return <JsonLd data={structuredData} />;
}

// スマートフォン、タブレット、PC表示の順に基本情報域と投稿コンテンツ域の項目と値の間の幅を調整
const PROFILE_LABEL_WIDTH = ['10em', '14em', '16em'];

// 医師登録年に表示する文字列を返す
const getLicensedLabel = (graduatedYearString: string) => {
  if (graduatedYearString === 'OTHER') {
    return 'その他';
  }

  // 先頭1文字に記号を含むかで分岐
  const yearStr = Number.isNaN(parseInt(graduatedYearString, 10))
    ? graduatedYearString.slice(1)
    : graduatedYearString;
  const graduatedYear = Number(yearStr);

  // 現在年月日の判定に使用
  const currentDate = dayjs();
  const currentYear = currentDate.year();

  // 1月1日
  const januaryStart = dayjs(`${currentYear}-01-01 00:00:00`).format();
  // 2月末
  const februaryEnd = dayjs(`${currentYear}-02-01`).endOf('month').format();

  // 現在年が医師登録年と一致　且つ　現在月日が1月1日~2月末の場合
  if (
    currentYear === graduatedYear &&
    currentDate.isBetween(januaryStart, februaryEnd, null, '[]')
  ) {
    return `${graduatedYear}年(卒業見込み)`;
  } else {
    return `${graduatedYear}年`;
  }
};

function SectionHeading({ children }: { children: string }) {
  return (
    <Heading size="md" mb={4}>
      {children}
    </Heading>
  );
}

function TwitterProfileCircleButton({
  twitterAccount,
}: {
  twitterAccount: string | null;
}) {
  if (!twitterAccount) {
    return null;
  }

  const onClick = () => sendAnalyticsEvent('link_to', 'twitter', 'profile');
  const linkUrl = `https://twitter.com/${twitterAccount}`;

  return (
    <Box h={6}>
      <ExternalLink href={linkUrl} withIcon={false}>
        <IconButton
          aria-label="Twitterプロフィールを開く"
          size="xs"
          rounded="full"
          backgroundColor="#fff"
          icon={<FaXTwitter size={18} color={'black'} />}
          onClick={onClick}
        />
      </ExternalLink>
    </Box>
  );
}

function AwardBanner() {
  return (
    <Text
      color="white"
      bg="awardGold"
      fontSize="sm"
      fontWeight="bold"
      textAlign="center"
      pos="absolute"
      top={['56px', null, '60px', '75px']}
      left={0}
      right={0}
      py={1}
    >
      Antaa Slide Award受賞者
    </Text>
  );
}

function UserProfile({
  user,
  isMyProfile,
  isCorporationAccount,
  isDoctorRecruiterAccount,
  departmentName,
  fullName,
}: {
  user: User;
  isMyProfile: boolean;
  isCorporationAccount: boolean;
  isDoctorRecruiterAccount: boolean;
  departmentName: string | null;
  fullName: string;
}) {
  const imageVersion = user.updateDateEpochMilliseconds;
  let profileImageUrl: string | undefined;
  if (user.hasProfileImage) {
    profileImageUrl = `/api/profile/${user.userId}/image?v=${imageVersion}`;
    if (user.idBaseProfileUrl) {
      profileImageUrl += `&idurl=${encodeURIComponent(user.idBaseProfileUrl)}`;
    }
  }
  let nameLabel = createNameLabel({ user, fullName });
  if (
    departmentName &&
    (isSlideAccount(user.accountType ?? '') ||
      user.antaaAccountType !== AntaaAccountType.CORPORATION)
  ) {
    nameLabel += ` - ${departmentName}`;
  }
  const isOfficial = user.isOfficialAccount ?? false;
  return (
    <Flex>
      <HStack spacing={10}>
        <Image
          src={profileImageUrl}
          fallbackSrc="/images/avatar_default_gray.png"
          w="4.5rem"
          h="4.5rem"
          alt={fullName}
          objectFit="cover"
          borderRadius="full"
          mt={1}
        />
        <Box pt={6}>
          <Flex justifyContent={'flex-start'}>
            <Heading
              as="h1"
              lineHeight={6}
              noOfLines={2}
              fontSize="normal"
              fontWeight="normal"
            >
              {nameLabel}
            </Heading>
            {isOfficial && (
              <Box ml={1} mt={0.5}>
                <HiCheckBadge size={20} color="#008e14" />
              </Box>
            )}
          </Flex>
          <TwitterProfileCircleButton twitterAccount={user.twitterAccount} />
        </Box>
      </HStack>
      <Spacer />
      {isMyProfile && (!isCorporationAccount || isDoctorRecruiterAccount) && (
        <EditProfileButtonLink />
      )}
    </Flex>
  );
}

function Achievements({
  postedArticleCount,
  viewedArticleCount,
  likedArticleCount,
}: {
  postedArticleCount: number;
  viewedArticleCount: number;
  likedArticleCount: number;
}) {
  const bgColor = useColorModeValue('white', 'gray.800'); // chakra-body-bg

  return (
    <>
      <Box borderRadius={6} bg={bgColor} p={4}>
        <HStack
          justify="space-around"
          spacing={0}
          divider={<StackDivider borderColor="lightgray" />}
          pb={2}
        >
          <VStack>
            <Text fontSize="sm" fontWeight="bold">
              投稿した数
            </Text>
            <Text pt={3} fontSize="sm" fontWeight="bold">
              <CommaFormattedNumber>{postedArticleCount}</CommaFormattedNumber>
            </Text>
          </VStack>
          <VStack>
            <Text fontSize="sm" fontWeight="bold">
              閲覧された数
            </Text>
            <Text pt={3} fontSize="sm" fontWeight="bold">
              <CommaFormattedNumber>{viewedArticleCount}</CommaFormattedNumber>
            </Text>
          </VStack>
          <VStack>
            <Text fontSize="sm" fontWeight="bold">
              保存された数
            </Text>
            <Text pt={3} fontSize="sm" fontWeight="bold">
              <CommaFormattedNumber>{likedArticleCount}</CommaFormattedNumber>
            </Text>
          </VStack>
        </HStack>
      </Box>
    </>
  );
}

function Badges({
  userId,
  isMyProfile,
  isPharmaceuticalAccount,
  badgeDisclosure,
}: {
  userId: number;
  isMyProfile: boolean;
  isPharmaceuticalAccount: boolean;
  badgeDisclosure: UseDisclosureReturn;
}) {
  return (
    <>
      {isMyProfile && !isPharmaceuticalAccount && (
        <>
          <Flex pt={4} justifyContent="flex-end">
            <Button
              variant="link"
              size="xs"
              _light={{
                color: 'gray.800',
              }}
              leftIcon={
                <NextImage
                  alt=""
                  src="/images/badge_star.svg"
                  width={16}
                  height={16}
                />
              }
              rightIcon={<ChevronRightIcon color="brand.500" fontSize={20} />}
              onClick={() => {
                sendAnalyticsEvent('badge', 'show', 'list');
                badgeDisclosure.onOpen();
              }}
            >
              バッジを見る
            </Button>
          </Flex>
        </>
      )}
      <BadgeModal userId={userId} disclosure={badgeDisclosure} />
    </>
  );
}

function UserBasicInfo({
  user,
  departmentName,
  subDepartmentNames,
  prefectureName,
}: {
  user: User;
  departmentName: string | null;
  subDepartmentNames: string[];
  prefectureName: string | null;
}) {
  return (
    <>
      {(user.graduatedYear ||
        !!user.hospital ||
        user.jobTitle ||
        departmentName ||
        user.subSpeciality) && <SectionHeading>基本情報</SectionHeading>}
      <Box w="100%">
        {user.graduatedYear && (
          <Box display="flex" pb={2} mb={4} borderBottom="1px solid lightgray">
            <Text w={PROFILE_LABEL_WIDTH}>医師登録年</Text>
            <Text>{getLicensedLabel(user.graduatedYear)}</Text>
          </Box>
        )}
        {!!user.hospital && (
          <Flex pb={2} mb={4} borderBottom="1px solid lightgray">
            <Text w={PROFILE_LABEL_WIDTH}>所属</Text>
            <Text overflowWrap="break-word">
              {user.hospital}
              {!!prefectureName && <>({prefectureName})</>}
            </Text>
          </Flex>
        )}
        {user.jobTitle && (
          <Flex pb={2} mb={4} borderBottom="1px solid lightgray">
            <Text w={PROFILE_LABEL_WIDTH}>役職</Text>
            <Text overflowWrap="break-word">{user.jobTitle}</Text>
          </Flex>
        )}
        {user.speciality && (
          <Flex pb={2} mb={4} borderBottom="1px solid lightgray">
            <Text w={PROFILE_LABEL_WIDTH}>専門</Text>
            <Text overflowWrap="break-word">{user.speciality}</Text>
          </Flex>
        )}
        {departmentName && (
          <Flex pb={2} mb={4} borderBottom="1px solid lightgray">
            <Text w={PROFILE_LABEL_WIDTH}>診療科</Text>
            <Text overflowWrap="break-word">{`${departmentName}${
              subDepartmentNames.length > 0
                ? '・' + subDepartmentNames.join('・')
                : ''
            }`}</Text>
          </Flex>
        )}
        {!user.speciality && user.subSpeciality && (
          <Flex pb={2} mb={4} borderBottom="1px solid lightgray">
            <Text w={PROFILE_LABEL_WIDTH}>専門</Text>
            <Text overflowWrap="break-word">{user.subSpeciality}</Text>
          </Flex>
        )}
      </Box>
    </>
  );
}

function AwardsInfo({ awards }: { awards: Award[] }) {
  const bgColor = useColorModeValue('white', 'gray.800'); // chakra-body-bg
  return (
    <VStack borderRadius={6} bg={bgColor} h="full" p={4} ps={6}>
      <HStack me={2} spacing={1}>
        <FaCrown size={16} color="gold" />
        <Text fontSize="sm" fontWeight="bold">
          受賞・選出歴
        </Text>
      </HStack>
      <HStack>
        {awards.map((award) => (
          <NextImage
            key={award.id}
            src={`/images/award/${award.imageFilename}`}
            alt={award.name}
            width={40}
            height={40}
          />
        ))}
      </HStack>
    </VStack>
  );
}

function InterviewsInfo({ interviews }: { interviews: Interview[] }) {
  return (
    <>
      <SectionHeading>インタビュー</SectionHeading>
      <SimpleGrid w="full" columns={[1, null, 2]} gap={2}>
        {interviews.map((interview) => (
          <Box key={interview.id} shadow="sm">
            <Link
              href={interview.siteUrl}
              isExternal
              onClick={() => {
                sendAnalyticsEvent(
                  'link_to',
                  'interview',
                  `${interview.id}_from_profile`,
                );
              }}
            >
              <Image src={interview.imageUrl} alt="インタビュー" />
            </Link>
          </Box>
        ))}
      </SimpleGrid>
    </>
  );
}

function SortOrderComponent({ sortOrder }: { sortOrder: SortOrder }) {
  const router = useRouter();
  const onSelectChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      router.push(
        `/profile/${router.query.userId ?? ''}?sortOrder=${e.target.value}`,
      );
    },
    [router],
  );

  return (
    <Flex justify="flex-end" wrap="wrap">
      <FormControl as={Flex} w="auto" align="center">
        <FormLabel m={0}>表示順：</FormLabel>
        <Select
          w="200px"
          fontSize="sm"
          value={sortOrder}
          onChange={onSelectChange}
          onFocus={() => sendAnalyticsEvent('slide', 'sort', null)}
        >
          <option value={SORT_ORDER_UPDATE_DATE_DESC}>
            公開日／更新日（新しい順）
          </option>
          <option value={SORT_ORDER_SESSION_COUNT_DESC}>
            ページ閲覧数（多い順）
          </option>
          <option value={SORT_ORDER_LIKE_COUNT_DESC}>
            保存済み数（多い順）
          </option>
        </Select>
      </FormControl>
    </Flex>
  );
}

function PostedContents({
  articles,
  sortOrder,
  isMyProfile,
}: {
  articles: ArticleSummaryProps[];
  sortOrder: SortOrder;
  isMyProfile: boolean;
}) {
  return (
    <>
      <Box w="full" mb={4}>
        <Flex
          justify="space-between"
          w="full"
          wrap="wrap"
          alignItems="center"
          gap={2}
        >
          <SectionHeading>投稿したスライド</SectionHeading>
          <SortOrderComponent sortOrder={sortOrder} />
        </Flex>
      </Box>
      {articles.length > 0 ? (
        <ArticlesGrid
          articles={articles}
          gaEventLabel={isMyProfile ? 'from_my_profile' : 'from_other_profile'}
          onShowThumbnail={(articleId) => {
            sendAnalyticsEvent(
              'slide_thumbnail',
              'show',
              `profile_postedArticles#${articleId}`,
            );
          }}
          onClick={(articleId) => {
            sendAnalyticsEvent(
              'slide_thumbnail',
              'click',
              `profile_postedArticles#${articleId}`,
            );
          }}
        />
      ) : (
        <Box w="100%">投稿したスライドはありません</Box>
      )}
    </>
  );
}

function LoginButtonLink(props: ButtonProps) {
  const router = useRouter();
  const p = new URLSearchParams();
  p.set('redirectUrl', router.asPath);
  const href = `/login?${p.toString()}`;

  const onClick = () => {
    sendAnalyticsEvent('induction', 'login', 'profile');
  };

  return (
    <NextLink href={href} passHref>
      <Button
        {...props}
        as="a"
        fontSize="sm"
        colorScheme="brand"
        variant="solid"
        onClick={onClick}
      >
        ログイン
      </Button>
    </NextLink>
  );
}

function SignupButtonLink(props: ButtonProps) {
  const { idBaseUrl } = getPublicRuntimeConfig();
  const accountSignupUrl = `${idBaseUrl}/signup?from_service=slide`;
  const onClick = () => {
    sendAnalyticsEvent('induction', 'signup', 'profile');
  };

  return (
    <NextLink href={`${accountSignupUrl}`} passHref>
      {/* アカウント登録は事前のID基盤ログインが不要なためproceedIdPageを使わない */}
      <Button
        {...props}
        as="a"
        fontSize="sm"
        colorScheme="brand"
        variant="outline"
        onClick={onClick}
      >
        アカウント登録
      </Button>
    </NextLink>
  );
}

function EditProfileButtonLink(props: ButtonProps) {
  const router = useRouter();
  const { apiClient } = useApiClient();
  const { proceedIdPage } = useIdApiClient();
  const { user } = useAppContext();
  const [href, setHref] = useState<string>('');
  const { idBaseUrl } = getPublicRuntimeConfig();

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient('/api/account/integration/status', {
          method: 'GET',
        });
        if (res.ok) {
          const data = await res.json();
          if (
            isAccountIntegrated(data.integrationStatus) &&
            !isSlideAccount(user?.accountType ?? '')
          ) {
            // アカウント統合済みの場合はID基盤のプロフィール編集画面に遷移させる
            setHref(`${idBaseUrl}/users/profile/edit?from_service=slide`);
          } else {
            // アカウント統合未済の場合はSlide Webのプロフィール編集画面に遷移させる
            setHref('/mypage/edit');
          }
        } else {
          // エラー時は遷移させない
          setHref('');
        }
      } catch (e) {
        console.error(e);
        // エラー時は遷移させない
        setHref('');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiClient, user?.accountType]);

  const fontColor = useColorModeValue('gray.500', 'gray.300');
  const bgColor = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.300', 'gray.500');

  const onClick = () => {
    if (!href) return;
    sendAnalyticsEvent('profile', 'click', 'edit_button');
    if (href.startsWith(idBaseUrl)) {
      proceedIdPage(href.substring(idBaseUrl.length), user?.discloseId);
    } else {
      router.push(href);
    }
  };

  return (
    <Button
      {...props}
      as="a"
      size="sm"
      fontSize="sm"
      color={fontColor}
      backgroundColor={bgColor}
      borderColor={borderColor}
      borderWidth="1px"
      variant="solid"
      onClick={onClick}
      cursor="pointer"
    >
      編集
    </Button>
  );
}

const PageUnavailableMsg = ({}) => {
  return (
    <Box mt={20}>
      <Container
        maxW="lg"
        p={4}
        borderWidth={1}
        borderColor="lightgray"
        borderRadius={8}
      >
        <Text textAlign="center">{`法人アカウントのため表示できません`}</Text>
      </Container>
    </Box>
  );
};

const ProfilePage: NextPage<PageContext> = ({
  user,
  isMyProfile,
  articles,
  sortOrder,
  postedArticleCount,
  viewedArticleCount,
  likedArticleCount,
  departmentName,
  subDepartmentNames,
  prefectureName,
  awards,
  interviews,
  isShowBadge,
  isDemoAccount,
  isCorporationAccount,
  isPharmaceuticalAccount,
  isPublisherAccount,
  isDoctorRecruiterAccount,
}: PageContext) => {
  const { proceedIdPage } = useIdApiClient();
  // Slideアカウント以外の場合はID基盤側のプロフィール画面にリダイレクトするか判定する
  const [isJudgeRedirecting, setIsJudgeRedirecting] = useState(
    !isSlideAccount(user.accountType ?? ''),
  );

  // リダイレクト判定後にイベント送信
  useEffect(() => {
    if (isJudgeRedirecting) return;
    if (isMyProfile) {
      sendAnalyticsEvent('profile', 'show', 'self');
    } else {
      sendAnalyticsEvent('profile', 'show', 'other');
    }
  }, [isJudgeRedirecting, isMyProfile]);

  const fullWidthSectionBgColor = useColorModeValue('#f8f8f8', 'black');
  const badgeDisclosure = useDisclosure();
  const router = useRouter();
  useEffect(() => {
    if (isShowBadge) {
      sendAnalyticsEvent('badge', 'show', 'list_from_link');
      badgeDisclosure.onOpen();
      router.push(
        router.asPath.replace(
          'defaultIsOpenBadgeModal=true',
          'defaultIsOpenBadgeModal=false',
        ),
      );
    }
  }, [badgeDisclosure, isShowBadge, router]);

  const fullName = createUserName(user);

  const {
    user: loginUser,
    isAuthenticated,
    isLicenseVerified,
    accountIntegrationStatus,
  } = useAppContext();

  // Slide WebとID基盤、どちらのプロフィール画面で表示するか判定
  // 表示対象：
  //    Antaaアカウント（Slideアカウントと統合済みの場合を含む）の場合 ⇒ ID基盤側のプロフィール画面へ遷移
  //    上記以外 ⇒ Slide Webのプロフィール画面で表示
  useEffect(() => {
    if (
      user.discloseId &&
      user.accountType &&
      (isAntaaAccount(user.accountType) ||
        (isSlideAndAntaaAccount(user.accountType) &&
          isAccountIntegrated(user.accountIntegrationStatus)))
    ) {
      const params: Record<string, string> = {
        from_service: 'slide',
        sort_order: sortOrder,
      };
      if (isSlideAccount(loginUser?.accountType ?? '')) {
        params['lisa'] = 'y';
      }
      if (hasDemoAuthority(loginUser?.userRoles)) {
        params['lida'] = 'y';
      }
      if (router.query.defaultIsOpenBadgeModal) {
        params['defaultIsOpenBadgeModal'] = router.query
          .defaultIsOpenBadgeModal as string;
      }
      if (router.query.from_edit) {
        params['from_edit'] = router.query.from_edit as string;
      }
      const query = new URLSearchParams(params).toString();
      const {
        publicRuntimeConfig: { idBaseUrl },
      } = getConfig();
      // アカウント統合済みの場合は認証済みCookie発行のため
      // proceedIdPageでID基盤にログインしてからリダイレクトする
      // ただし非ログインでもプロフィール画面閲覧可能のためID基盤ログインに失敗してもリダイレクトする
      if (isAccountIntegrated(accountIntegrationStatus)) {
        proceedIdPage(
          `/profile/${user.discloseId}?${query}`,
          loginUser?.discloseId,
          {
            skipLoginRedirect: true,
            replace: true,
          },
        );
      }
      // 非ログインまたはアカウント統合未済の場合はID基盤へのログインはせず
      // リダイレクトのみ行う
      else {
        window.location.replace(
          `${idBaseUrl}/profile/${user.discloseId}?${query}`,
        );
      }
    } else {
      setIsJudgeRedirecting(false);
    }
  }, [
    accountIntegrationStatus,
    isAuthenticated,
    loginUser?.accountType,
    loginUser?.discloseId,
    loginUser?.userRoles,
    proceedIdPage,
    router.query.defaultIsOpenBadgeModal,
    router.query.from_edit,
    sortOrder,
    user.accountType,
    user.antaaAccountType,
    user.discloseId,
  ]);

  if (isJudgeRedirecting) {
    return null;
  }
  if (!isAuthenticated) {
    return (
      <>
        <PageHead
          user={user}
          departmentName={departmentName}
          subDepartmentNames={subDepartmentNames}
          fullName={fullName}
        />
        {awards.length > 0 && <AwardBanner />}
        <Box>
          <UserProfile
            user={user}
            isMyProfile={isMyProfile}
            isCorporationAccount={isCorporationAccount}
            isDoctorRecruiterAccount={isDoctorRecruiterAccount}
            departmentName={departmentName}
            fullName={fullName}
          />
        </Box>
        <Box mt={8}>
          <Container
            maxW="lg"
            p={4}
            borderWidth={1}
            borderColor="lightgray"
            borderRadius={8}
          >
            <Text textAlign="center">
              詳しいプロフィールをご覧になるにはログインして下さい。
            </Text>
            <HStack justify="center" mt={4}>
              <LoginButtonLink flex={1} maxW={160} />
              <SignupButtonLink flex={1} maxW={160} />
            </HStack>
          </Container>
        </Box>
      </>
    );
  }
  const isOtherDoctorRecruiter =
    isDoctorRecruiterAccount &&
    user.antaaAccountSubType !== AntaaAccountSubType.DOCTOR_RECRUITER;
  const isBlockedCorporateViewer =
    user.antaaAccountType === AntaaAccountType.CORPORATION &&
    (isPublisherAccount || isOtherDoctorRecruiter);
  const shouldNotShowProfileViewer =
    !isMyProfile && (isPharmaceuticalAccount || isBlockedCorporateViewer);
  if (shouldNotShowProfileViewer) {
    return <PageUnavailableMsg />;
  }
  if (isAuthenticated && !isLicenseVerified && !isMyProfile) {
    return (
      <>
        <PageHead
          user={user}
          departmentName={departmentName}
          subDepartmentNames={subDepartmentNames}
          fullName={fullName}
        />
        {awards.length > 0 && <AwardBanner />}
        <Box>
          <UserProfile
            user={user}
            isMyProfile={isMyProfile}
            isCorporationAccount={isCorporationAccount}
            isDoctorRecruiterAccount={isDoctorRecruiterAccount}
            departmentName={departmentName}
            fullName={fullName}
          />
        </Box>
        <Box mt={8}>
          <Container
            maxW="lg"
            p={4}
            borderWidth={1}
            borderColor="lightgray"
            borderRadius={8}
          >
            <Text textAlign="center">
              詳しいプロフィールをご覧になるには医師/医学生認証して下さい。
            </Text>
            <Center>
              <Button
                fontSize="sm"
                colorScheme="brand"
                variant="solid"
                onClick={async () => {
                  // prettier-ignore
                  sendAnalyticsEvent('induction', 'doctor_certification', 'profile');
                  await proceedIdPage(
                    `/users/doctor/register?from_service=slide`,
                  );
                }}
              >
                医師/医学生認証を行う
              </Button>
            </Center>
          </Container>
        </Box>
      </>
    );
  }
  if (isDemoAccount) {
    return (
      <Box mt={8}>
        <Container
          maxW="xl"
          p={4}
          borderWidth={1}
          borderColor="lightgray"
          borderRadius={8}
        >
          <Text textAlign="center">
            デモアカウントのため、プロフィールは閲覧できません
          </Text>
        </Container>
      </Box>
    );
  }

  return (
    <>
      <PageHead
        user={user}
        departmentName={departmentName}
        subDepartmentNames={subDepartmentNames}
        fullName={fullName}
      />
      {awards.length > 0 && <AwardBanner />}
      <Box>
        <UserProfile
          user={user}
          isMyProfile={isMyProfile}
          isCorporationAccount={isCorporationAccount}
          isDoctorRecruiterAccount={isDoctorRecruiterAccount}
          departmentName={departmentName}
          fullName={fullName}
        />
      </Box>
      <FullWidthBox bg={fullWidthSectionBgColor} mt={4}>
        <Container py={4}>
          <Flex flexDir={['column', null, 'row']} gap={2}>
            {awards.length > 0 && (
              <Box flexShrink={0}>
                <AwardsInfo awards={awards} />
              </Box>
            )}
            <Box w="full">
              <Achievements
                postedArticleCount={postedArticleCount}
                viewedArticleCount={viewedArticleCount}
                likedArticleCount={likedArticleCount}
              />
            </Box>
          </Flex>
          <Badges
            userId={user.userId}
            isMyProfile={isMyProfile}
            isPharmaceuticalAccount={isPharmaceuticalAccount}
            badgeDisclosure={badgeDisclosure}
          />
        </Container>
      </FullWidthBox>
      <Box mt={8}>
        <UserBasicInfo
          user={user}
          departmentName={departmentName}
          subDepartmentNames={subDepartmentNames}
          prefectureName={prefectureName}
        />
      </Box>
      {interviews.length > 0 && (
        <Box mt={8}>
          <InterviewsInfo interviews={interviews} />
        </Box>
      )}
      <Box mt={-20}>
        <Box id="postedContents" pb={24} />
        <PostedContents
          sortOrder={sortOrder}
          articles={articles}
          isMyProfile={isMyProfile}
        />
      </Box>
    </>
  );
};

export default ProfilePage;

/**
 * ユーザー取得
 */
async function findUserById(userId: number) {
  return await prisma.user.findFirst({
    where: {
      user_id: userId,
      is_deleted: false,
    },
  });
}
async function findUserByDiscloseId(discloseId: string) {
  return await prisma.user.findFirst({
    where: {
      disclose_id: discloseId,
      is_deleted: false,
    },
  });
}

/**
 * 診療科取得
 */
async function findDepartmentById(departmentId: number) {
  return await prisma.departments.findUnique({
    where: {
      id: departmentId,
    },
  });
}

/**
 * 診療科取得
 */
async function findDepartmentByIds(departmentIds: number[]) {
  return await prisma.departments.findMany({
    where: {
      id: {
        in: departmentIds,
      },
    },
  });
}

/**
 * 都道府県取得
 */
async function findPrefectureById(prefectureId: number) {
  return await prisma.prefectures.findUnique({
    where: {
      id: prefectureId,
    },
  });
}

/**
 * ユーザーが投稿したスライド一覧を取得
 */
async function findArticlesByAuthorId(
  authorId: number,
  sortTargetColumn: SortTargetColumn,
) {
  return await prisma.article_header
    .findMany({
      select: {
        article_id: true,
        name: true,
        article_type: true,
        status: true,
        top_img_m: true,
        session_count: true,
        like_count: true,
        disclose_scope: true,
        create_date: true,
        update_date: true,
        first_publish_date: true,
        article_page: {
          select: {
            like_count: true,
          },
        },
        user_article_header_author_idTouser: true,
        article_tag_connection: {
          select: {
            tag: true,
          },
        },
        article_comments: {
          select: {
            id: true,
          },
        },
      },
      where: {
        author_id: authorId,
        status: 'DONE',
        hidden: false,
        is_deleted: false,
      },
      orderBy: {
        [sortTargetColumn]: 'desc',
      },
    })
    .then((articles) =>
      articles.map((article) => ({
        ...article,
        commentCount: article.article_comments.length,
      })),
    );
}

async function findAwardsByUserId(userId: number) {
  return await prisma.awards.findMany({
    where: {
      user_has_awards: {
        some: {
          user_id: userId,
        },
      },
    },
    orderBy: {
      year: 'desc',
    },
  });
}

async function findInterviewsByUserId(userId: number) {
  return await prisma.interviews.findMany({
    where: {
      user_has_interviews: {
        some: {
          user_id: userId,
        },
      },
    },
  });
}

async function createUserAccessHistories(
  userId: number | null,
  userAgent: string | null,
  targetUserId: number,
) {
  await prisma.user_access_histories.create({
    data: {
      user_id: userId,
      user_agent: userAgent,
      operation: 'profile',
      target_user_id: targetUserId,
    },
  });
}

export const getServerSideProps: GetServerSideProps<PageContext> = withAuth(
  async (context) => {
    let sortOrder: SortOrder;
    switch (context.query.sortOrder) {
      case SORT_ORDER_LIKE_COUNT_DESC:
      case SORT_ORDER_SESSION_COUNT_DESC:
        sortOrder = context.query.sortOrder;
        break;
      default:
        sortOrder = SORT_ORDER_UPDATE_DATE_DESC;
        break;
    }

    let sortTargetColumn: SortTargetColumn;
    switch (sortOrder) {
      case SORT_ORDER_LIKE_COUNT_DESC:
        sortTargetColumn = SORT_TARGET_COLUMN_LIKE_COUNT;
        break;
      case SORT_ORDER_SESSION_COUNT_DESC:
        sortTargetColumn = SORT_TARGET_COLUMN_SESSION_COUNT;
        break;
      case SORT_ORDER_UPDATE_DATE_DESC:
        sortTargetColumn = SORT_TARGET_COLUMN_UPDATE_DATE;
        break;
      default:
        sortTargetColumn = SORT_TARGET_COLUMN_UPDATE_DATE;
        break;
    }

    let user: Prisma.userGetPayload<Prisma.userFindFirstArgs> | null = null;
    let userId: number | string | undefined = yup
      .number()
      .required()
      .cast(context.params?.userId, { assert: false });
    if (userId) {
      user = await findUserById(userId);
      if (!user) {
        // discloseIdが全桁数字の場合を考慮してdiscloseIdでも検索する
        user = await findUserByDiscloseId(String(userId));
      }
    } else {
      userId = yup
        .string()
        .required()
        .cast(context.params?.userId, { assert: false });
      if (userId) {
        user = await findUserByDiscloseId(userId);
      }
    }
    if (!user) {
      return {
        notFound: true,
      };
    }
    userId = user.user_id;

    let articles = await findArticlesByAuthorId(userId, sortTargetColumn);
    // 更新日順の場合は初公開日時も考慮した順で並び替える
    if (sortOrder === SORT_ORDER_UPDATE_DATE_DESC) {
      articles = articles.sort((a, b) => {
        let updateDate = a.update_date.getTime();
        let firstPublishDate = a.first_publish_date
          ? a.first_publish_date.getTime()
          : 0;
        const aTime = Math.max(updateDate, firstPublishDate);
        updateDate = b.update_date.getTime();
        firstPublishDate = b.first_publish_date
          ? b.first_publish_date.getTime()
          : 0;
        const bTime = Math.max(updateDate, firstPublishDate);
        return bTime - aTime;
      });
    }

    // アカウント統合済みかどうかでプロフィール情報の取得元を切り替える
    // アカウント統合済み: ID基盤のプロフィールを表示
    // アカウント統合未済: Slide DBのプロフィールを表示
    let discloseIds = articles
      .map((v) => v.user_article_header_author_idTouser.disclose_id ?? '')
      .filter((v) => !!v);
    if (user.disclose_id) {
      discloseIds.push(user.disclose_id);
    }
    // アカウント統合済みのユーザのプロフィールのみ返却される
    const idBaseProfileMap = await fetchAccountIntegrationProfiles(discloseIds);
    const idBaseProfile = idBaseProfileMap[user.disclose_id ?? ''];

    /* SEO対策を優先するため、リダイレクト処理を無効化
    // スライド投稿数が0のユーザーのプロフィールを非ログインユーザーが閲覧しようとした場合はログイン画面へリダイレクトする
    if (context.req.isUnauthenticated() && articles.length === 0) {
      const p = new URLSearchParams();
      if (context.params?.userId) {
        p.set('redirectUrl', `/profile/${context.params?.userId}`);
      }
      return {
        redirect: {
          permanent: false,
          destination: `/login?${p.toString()}`,
        },
      };
    }
    */

    // 表示しているプロフィール画面のユーザーとログインユーザーが一致するか
    const isMyProfile = context.req.user?.id === user.user_id;
    let isShowBadge;
    if (isMyProfile) {
      isShowBadge = yup
        .boolean()
        .default(false)
        .validateSync(context.query?.defaultIsOpenBadgeModal);
    } else {
      isShowBadge = false;

      if (
        shouldNotShow(
          idBaseProfile,
          context.req.user,
          context.req.isAuthenticated(),
          null,
        )
      ) {
        context.req.log.error('hidden except account type');
        return {
          notFound: true,
        };
      }
    }

    const isDemoAccount = hasDemoAuthority(context.req.user?.userRoles);

    // アカウント統合完了時はID基盤側のプロフィールに設定されたdepartment_idを用いるが
    // departmentsのテーブルマスタ情報はSlideとID基盤（QA）で等しいため
    // Slide DBから取得する
    let departmentPromise: ReturnType<typeof findDepartmentById> =
      Promise.resolve(null);
    if (idBaseProfile?.departmentId) {
      departmentPromise = findDepartmentById(idBaseProfile.departmentId);
    }
    let subDepartmentsPromise: ReturnType<typeof findDepartmentByIds> =
      Promise.resolve([]);
    if (idBaseProfile?.subDepartmentIds) {
      subDepartmentsPromise = findDepartmentByIds(
        idBaseProfile.subDepartmentIds,
      );
    }
    // アカウント統合完了時はID基盤側のプロフィールに設定されたprefecture_idを用いるが
    // prefecturesのテーブルマスタ情報はSlideとID基盤（QA）で等しいため
    // Slide DBから取得する
    let prefecturePromise: ReturnType<typeof findPrefectureById> =
      Promise.resolve(null);
    if (idBaseProfile?.prefectureId && idBaseProfile.prefectureId !== 999) {
      prefecturePromise = findPrefectureById(idBaseProfile.prefectureId);
    }

    const awardsPromise = findAwardsByUserId(userId);
    const interviewsPromise = findInterviewsByUserId(userId);

    let [department, subDepartments, prefecture, awards, interviews] =
      await Promise.all([
        departmentPromise,
        subDepartmentsPromise,
        prefecturePromise,
        awardsPromise,
        interviewsPromise,
      ]);
    // 診療科はpriority順にソートする
    // 実際のソートはAPI側でやっているので、APIから返された順で表示する
    if (idBaseProfile?.subDepartmentIds) {
      subDepartments.sort(
        (a, b) =>
          idBaseProfile.subDepartmentIds.findIndex((v: number) => v === a.id) -
          idBaseProfile.subDepartmentIds.findIndex((v: number) => v === b.id),
      );
    }

    let departmentName: string | null = null;
    if (idBaseProfile) {
      if (
        department?.id === DEPARTMENT_ID_OTHER &&
        !!idBaseProfile.otherDepartment
      ) {
        departmentName = idBaseProfile.otherDepartment;
      } else {
        departmentName = department?.department_name_ja ?? null;
      }
    } else {
      if (
        user.department_id === DEPARTMENT_ID_OTHER &&
        !!user.other_department
      ) {
        departmentName = user.other_department;
      } else if (user.department_id) {
        department = await findDepartmentById(user.department_id);
        departmentName = department?.department_name_ja ?? null;
      }
    }

    const prefectureName =
      prefecture?.prefecture_name ?? idBaseProfile?.prefectureName ?? null;

    // ユーザー投稿スライドが閲覧された数。
    const viewedArticleCount = articles.reduce(
      (total, article) => total + (article.session_count || 0),
      0,
    );

    // ユーザー投稿スライドが保存された数。スライド保存数とページ保存数の合算。
    const likedArticleCount = articles.reduce(
      (total, article) =>
        total +
        article.like_count +
        article.article_page.reduce(
          (ptotal, page) => ptotal + page.like_count,
          0,
        ),
      0,
    );

    createUserAccessHistories(
      context.req.user?.id ?? null,
      context.req.headers['user-agent'] ?? null,
      userId,
    );

    // アカウント統合完了時はID基盤側のプロフィールに設定されたhospital_idを用いるが
    // hospitalsのテーブルマスタ情報はSlideとID基盤（QA）で等しいため
    // Slide DBから取得する
    let hospitalName: string | null = null;
    if (idBaseProfile?.hospitalId) {
      const hospital = await prisma.hospitals.findUnique({
        where: {
          disclose_id: idBaseProfile.hospitalId,
        },
        select: {
          disclose_id: true,
          name: true,
        },
      });
      hospitalName = hospital?.name ?? null;
    }
    hospitalName ??= idBaseProfile?.hospitalName;
    // ID基盤側のIDでレコード取得できなかった場合はSlide側のIDでフォールバックさせる
    if (!hospitalName && user.hospital_id) {
      const hospital = await prisma.hospitals.findUnique({
        where: {
          id: user.hospital_id,
        },
        select: {
          disclose_id: true,
          name: true,
        },
      });
      hospitalName = hospital?.name ?? null;
    }
    hospitalName ??= user.hospital;

    if (context.req.isUnauthenticated()) {
      // 非ログインユーザーへ返却して良いのは以下の属性のみ
      // - ユーザーID
      // - 氏名
      // - プロフィール画像
      // - Twitterユーザー名
      // - 診療科
      // - 所属
      // - 専門
      // - 役職
      const profilePhotoKey =
        idBaseProfile?.profilePhotoUrl ?? user.profile_photo_s3_key;
      return {
        props: {
          user: {
            userId: user.user_id,
            discloseId: user.disclose_id,
            userName: idBaseProfile?.userName ?? null,
            // userNameが設定されている場合はfirstName, lastNameはフロントに返さないようにする
            lastName: idBaseProfile?.userName
              ? null
              : idBaseProfile?.lastName ?? user.last_name,
            firstName: idBaseProfile?.userName
              ? null
              : idBaseProfile?.firstName ?? user.first_name,
            hospital: hospitalName,
            prefectureId: null,
            speciality: idBaseProfile
              ? idBaseProfile?.speciality
              : user.speciality,
            subSpeciality: null,
            departmentId: null,
            twitterAccount: idBaseProfile
              ? idBaseProfile?.twitterAccount
              : user.twitter_account,
            jobTitle: idBaseProfile ? null : user.job_title,
            graduatedYear: null,
            hasProfileImage: !!profilePhotoKey,
            idBaseProfileUrl: idBaseProfile?.profilePhotoUrl ?? null,
            updateDateEpochMilliseconds: user.update_date.getTime(),
            accountType: accountTypeToString(user.account_type),
            antaaAccountType: idBaseProfile?.accountType ?? null,
            antaaAccountSubType: idBaseProfile?.accountSubType ?? null,
            isOfficialAccount: idBaseProfile?.isOfficialAccount ?? null,
            accountIntegrationStatus: idBaseProfile?.accountIntegrationStatus ?? null,
          },
          isMyProfile: false,
          departmentName,
          subDepartmentNames: [],
          prefectureName: null,
          articles: [],
          sortOrder,
          postedArticleCount: 0,
          viewedArticleCount: 0,
          likedArticleCount: 0,
          awards: [],
          interviews: [],
          isShowBadge: false,
          isDemoAccount: false,
          isCorporationAccount: false,
          isPharmaceuticalAccount: false,
          isPublisherAccount: false,
          isDoctorRecruiterAccount: false,
        } as PageContext,
      };
    }

    const profilePhotoKey =
      idBaseProfile?.profilePhotoUrl ?? user.profile_photo_s3_key;
    return {
      props: {
        user: {
          userId: user.user_id,
          discloseId: user.disclose_id,
          userName: idBaseProfile?.userName ?? null,
          // userNameが設定されている場合はfirstName, lastNameはフロントに返さないようにする
          lastName: idBaseProfile?.userName
            ? null
            : idBaseProfile?.lastName ?? user.last_name,
          firstName: idBaseProfile?.userName
            ? null
            : idBaseProfile?.firstName ?? user.first_name,
          hospital: hospitalName,
          prefectureId: idBaseProfile?.prefectureId ?? user.prefecture_id,
          speciality: idBaseProfile
            ? idBaseProfile?.speciality
            : user.speciality,
          subSpeciality: idBaseProfile ? null : user.sub_speciality,
          departmentId: idBaseProfile?.departmentId ?? user.department_id,
          twitterAccount: idBaseProfile
            ? idBaseProfile?.twitterAccount
            : user.twitter_account,
          jobTitle: idBaseProfile ? null : user.job_title,
          graduatedYear: idBaseProfile?.licensedYear ?? user.graduated_year,
          hasProfileImage: !!profilePhotoKey,
          idBaseProfileUrl: idBaseProfile?.profilePhotoUrl ?? null,
          updateDateEpochMilliseconds: user.update_date.getTime(),
          accountType: accountTypeToString(user.account_type),
          antaaAccountType: idBaseProfile?.accountType ?? null,
          antaaAccountSubType: idBaseProfile?.accountSubType ?? null,
          isOfficialAccount: idBaseProfile?.isOfficialAccount ?? null,
          accountIntegrationStatus: idBaseProfile?.accountIntegrationStatus ?? null,
        },
        isMyProfile,
        departmentName,
        subDepartmentNames: subDepartments.map((v) => v.department_name_ja),
        prefectureName,
        articles: articles.map((article) => {
          const idBaseProfile =
            idBaseProfileMap[
              article.user_article_header_author_idTouser.disclose_id ?? ''
            ];
          return {
            articleId: article.article_id,
            name: article.name,
            articleType: article.article_type as ArticleType,
            status: article.status as ArticleStatus | null,
            topImageM: article.top_img_m,
            sessionCount: article.session_count || 0,
            likeCount:
              article.like_count +
              article.article_page.reduce(
                (prev, curr) => prev + curr.like_count,
                0,
              ),
            commentCount: article.commentCount,
            discloseScope: article.disclose_scope as DiscloseScope | null,
            createDate: date.toIso8601Jst(article.create_date),
            updateDate: date.toIso8601Jst(article.update_date),
            author: {
              userId: article.user_article_header_author_idTouser.user_id,
              userName: idBaseProfile?.userName ?? null,
              // userNameが設定されている場合はfirstName, lastNameはフロントに返さないようにする
              lastName: idBaseProfile?.userName
                ? null
                : idBaseProfile?.lastName ??
                  article.user_article_header_author_idTouser.last_name,
              firstName: idBaseProfile?.userName
                ? null
                : idBaseProfile?.firstName ??
                  article.user_article_header_author_idTouser.first_name,
              updateDateEpochMilliseconds:
                article.user_article_header_author_idTouser.update_date.getTime(),
              profilePhotoS3Key:
                article.user_article_header_author_idTouser
                  .profile_photo_s3_key,
              idBaseProfileUrl: idBaseProfile?.profilePhotoUrl ?? null,
              accountType: accountTypeToString(
                article.user_article_header_author_idTouser.account_type,
              ),
              antaaAccountType: idBaseProfile?.accountType ?? null,
              antaaAccountSubType: idBaseProfile?.accountSubType ?? null,
              isOfficialAccount: idBaseProfile?.isOfficialAccount ?? null,
            },
            tags: article.article_tag_connection.map((conn) => ({
              id: conn.tag.tag_id,
              name: conn.tag.tag_name,
            })),
          };
        }),
        sortOrder,
        postedArticleCount: articles.length,
        viewedArticleCount,
        likedArticleCount,
        awards: awards.map((award) => ({
          id: award.id,
          name: award.name,
          imageFilename: award.image_filename,
        })),
        interviews: interviews.map((interview) => ({
          id: interview.id,
          siteUrl: interview.site_url,
          imageUrl: interview.image_url,
        })),
        isShowBadge,
        isDemoAccount,
        isCorporationAccount: isCorporationAccount(context.req.user),
        isPharmaceuticalAccount: isPharmaceuticalAccount(context.req.user),
        isPublisherAccount: isPublisherAccount(context.req.user),
        isDoctorRecruiterAccount: isDoctorRecruiterAccount(context.req.user),
      },
    };
  },
);
